export interface VariantAware {
  id: string;
  name: string;
  sku: string | null;
  parentSku?: string | null;
  variantAxes?: Record<string, unknown> | null;
}

export type VariantEntry<T extends VariantAware> =
  | { kind: 'single'; product: T }
  | {
      kind: 'group';
      parentSku: string;
      baseName: string;
      axisKey: string;
      axisLabel: string;
      options: { value: string; label: string; product: T }[];
    };

const AXIS_LABELS: Record<string, string> = {
  insulation: 'Insulation',
  capacity_l: 'Capacity',
  grade: 'Grade',
  thickness_mm: 'Thickness',
  size_mm: 'Size',
  sizes_mm: 'Size',
  thread_inch: 'Thread',
};

export function humanizeAxisKey(key: string): string {
  if (AXIS_LABELS[key]) return AXIS_LABELS[key];
  const base = key.replace(/_(l|mm)$/i, '').replace(/_/g, ' ').trim();
  return base.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatAxisValue(key: string, value: unknown): string {
  if (Array.isArray(value)) {
    const joined = value.map(String).join(' × ');
    return /_mm$/i.test(key) ? `${joined} mm` : joined;
  }
  if (/_inch$/i.test(key) && value != null && value !== '') {
    return `${String(value).replace(/"/g, '').trim()}"`;
  }
  if (typeof value === 'number') {
    if (/_l$/i.test(key)) return `${value} L`;
    if (/_mm$/i.test(key)) return `${value} mm`;
  }
  return String(value);
}

function commonPrefix(names: string[]): string {
  if (names.length === 0) return '';
  let p = names[0]!;
  for (const n of names.slice(1)) {
    let i = 0;
    while (i < p.length && i < n.length && p[i] === n[i]) i++;
    p = p.slice(0, i);
  }
  return p.replace(/[\s\-–·,(]+$/, '').trim();
}

// Comparable form of an axis value: arrays joined, stray `"` stripped (a live
// SS Plumbing row carries fitting_type `Tee - Male Thread"`, which otherwise
// reads as a differing axis).
function axisValueKey(v: unknown): string {
  if (Array.isArray(v)) return v.map(String).join(' × ');
  return String(v).replace(/"/g, '').trim();
}

// Every variantAxes key whose value differs across members, in first-seen key
// order. A label built from only the first one repeats: SS Plumbing threaded
// fittings showed "15 mm" once per thread size.
function findDifferingAxes<T extends VariantAware>(members: T[]): string[] {
  const keys = new Set<string>();
  for (const m of members) for (const k of Object.keys(m.variantAxes ?? {})) keys.add(k);
  const out: string[] = [];
  for (const k of keys) {
    const vals = new Set(members.map((m) => axisValueKey((m.variantAxes ?? {})[k])));
    if (vals.size > 1) out.push(k);
  }
  return out;
}

// `( Male )` union adapters share parentSku PLB-union-adapter (and
// fitting_type) with the plain ones in the live catalogue. Split them into
// their own card here instead of depending on a DB fix. After the 2026-09-14
// prod correction those rows carry parentSku PLB-union-adapter-male, which
// already splits them, so the suffix is added only when the key lacks it.
const MALE_SKU_RE = /\(\s*male\s*\)/i;
const MALE_KEY_RE = /(^|[^a-z])male\)?\s*$/i;

// SS Plumbing card order Sun Stellar asked for (Manak, 2026-09-14), same as
// the mobile picker. Other PLB- families follow A-Z by parentSku, which keeps
// the Male union right after the plain one. Only the PLB- slots are reordered;
// every other entry keeps its position.
const PLUMBING_ORDER = [
  'PLB-equal-elbow',
  'PLB-equal-tee',
  'PLB-female-thread-adapter',
  'PLB-male-thread-adapter',
  'PLB-reducing-tee',
  'PLB-female-thread-elbow',
  'PLB-male-thread-elbow',
  'PLB-reducing-elbow',
  'PLB-obtuse-elbow',
  'PLB-straight-coupling',
  'PLB-reducer-coupling-both-end',
  'PLB-reducer-coupling-plain-end',
];
const isPlumbingKey = (k: string) => /^PLB-/i.test(k);

function sortPlumbingKeys(order: string[]): void {
  const slots = order.flatMap((k, i) => (isPlumbingKey(k) ? [i] : []));
  if (slots.length < 2) return;
  const rank = (k: string) => {
    const i = PLUMBING_ORDER.indexOf(k.toLowerCase().replace(/^plb-/, 'PLB-'));
    return i < 0 ? PLUMBING_ORDER.length : i;
  };
  const keys = slots
    .map((i) => order[i]!)
    .sort((a, b) => rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0));
  slots.forEach((slot, j) => {
    order[slot] = keys[j]!;
  });
}

export function groupProductsByVariant<T extends VariantAware>(items: T[]): VariantEntry<T>[] {
  // Preserve first-appearance order of each parentSku / singleton.
  const order: string[] = [];
  const buckets = new Map<string, T[]>();
  const singles: VariantEntry<T>[] = [];

  for (const it of items) {
    const rawPs = it.parentSku?.trim();
    const ps =
      rawPs && MALE_SKU_RE.test(it.sku ?? '') && !MALE_KEY_RE.test(rawPs) ? `${rawPs} (Male)` : rawPs;
    if (!ps) {
      singles.push({ kind: 'single', product: it });
      order.push(`__single__${singles.length - 1}`);
      continue;
    }
    if (!buckets.has(ps)) {
      buckets.set(ps, []);
      order.push(ps);
    }
    buckets.get(ps)!.push(it);
  }
  sortPlumbingKeys(order);

  const out: VariantEntry<T>[] = [];
  let singleIdx = 0;
  for (const key of order) {
    if (key.startsWith('__single__')) {
      out.push(singles[singleIdx++]!);
      continue;
    }
    const members = buckets.get(key)!;
    const axisKeys = members.length >= 2 ? findDifferingAxes(members) : [];
    if (axisKeys.length === 0) {
      for (const m of members) out.push({ kind: 'single', product: m });
      continue;
    }
    out.push({
      kind: 'group',
      parentSku: key,
      // Guard against degenerate prefixes: "11 KW ..." vs "15 KW ..." share only
      // "1", which shipped a product card literally titled "1" (seen live in the
      // HEAT PUMP category). A base name that short identifies nothing — fall
      // back to the parent SKU instead.
      baseName: (() => {
        const prefix = commonPrefix(members.map((m) => m.name));
        return prefix.length >= 4 ? prefix : key;
      })(),
      axisKey: axisKeys[0]!,
      axisLabel: axisKeys.map(humanizeAxisKey).join(' × '),
      // One label per member from ALL differing axes, so no label repeats.
      options: members.map((m) => {
        const axes = m.variantAxes ?? {};
        return {
          value: axisKeys.map((k) => axisValueKey(axes[k])).join(' | '),
          label: axisKeys.map((k) => formatAxisValue(k, axes[k])).join(' × '),
          product: m,
        };
      }),
    });
  }
  return out;
}
